/**
 * PdfPreviewModal
 * Modal for previewing invoice/devis/credit note PDFs
 * Inspired by EmailContentModal design
 *
 * Updated: Integration Status Strip with compact status tiles
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  XMarkIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  CurrencyEuroIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  BuildingLibraryIcon,
  GlobeAltIcon,
  IdentificationIcon,
  BanknotesIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import './PdfPreviewModal.css';

export type DocumentType = 'invoice' | 'devis' | 'creditNote';

// Falco sync status
type FalcoStatus = 'PENDING' | 'UPLOADED' | 'PROCESSING' | 'SENT' | 'FAILED' | null;

// Peppol delivery status
type PeppolStatus = 'PENDING' | 'DELIVERED' | 'FAILED' | 'NOT_REGISTERED' | null;

interface DocumentInfo {
  type: DocumentType;
  id: number;
  number: string;
  date: string;
  clientName: string;
  amount: number;
  status: string;
  hasPdf: boolean;
  // Extended invoice fields
  billingVatNumber?: string | null;
  structuredCommunication?: string | null;
  falcoStatus?: FalcoStatus;
  falcoDocumentId?: string | null;
  falcoSyncedAt?: string | null;
  falcoError?: string | null;
  peppolStatus?: PeppolStatus;
  peppolDeliveredAt?: string | null;
  peppolError?: string | null;
  dueDate?: string | null;
}

interface PdfPreviewModalProps {
  document: DocumentInfo;
  onClose: () => void;
  onDownload: () => void;
  /** Use preview endpoint for draft invoices (generates PDF with watermark) */
  previewMode?: boolean;
  /** Optional back button callback - when provided, shows a back button in the header */
  onBack?: () => void;
  /** Label for back button (default: "Retour") */
  backLabel?: string;
  /** Optional validate callback for DRAFT documents */
  onValidate?: (sendEmail: boolean) => Promise<void>;
  /** Show validation controls */
  showValidation?: boolean;
  /** Is validation in progress */
  isValidating?: boolean;
}

export const PdfPreviewModal: React.FC<PdfPreviewModalProps> = ({
  document,
  onClose,
  onDownload,
  previewMode = false,
  onBack,
  backLabel = 'Retour',
  onValidate,
  showValidation = false,
  isValidating = false,
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendEmailOnValidate, setSendEmailOnValidate] = useState(true);

  const getDocumentTitle = () => {
    switch (document.type) {
      case 'invoice':
        return 'Facture';
      case 'devis':
        return 'Devis';
      case 'creditNote':
        return 'Note de Credit';
      default:
        return 'Document';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'd MMMM yyyy', { locale: fr });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-BE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Status badge configuration
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return { label: 'Brouillon', className: 'status-draft' };
      case 'GENERATED':
        return { label: 'Genere', className: 'status-generated' };
      case 'SENT':
        return { label: 'Envoye', className: 'status-sent' };
      case 'PAID':
        return { label: 'Paye', className: 'status-paid' };
      case 'CANCELLED':
        return { label: 'Annule', className: 'status-cancelled' };
      default:
        return { label: status, className: 'status-default' };
    }
  };

  // Falco status configuration
  const getFalcoStatusConfig = (status: FalcoStatus) => {
    switch (status) {
      case 'PENDING':
        return { label: 'En attente', icon: ClockIcon, className: 'falco-pending' };
      case 'UPLOADED':
        return { label: 'Uploade', icon: CheckCircleIcon, className: 'falco-uploaded' };
      case 'PROCESSING':
        return { label: 'En traitement', icon: ArrowPathIcon, className: 'falco-processing' };
      case 'SENT':
        return { label: 'Envoye', icon: CheckCircleIcon, className: 'falco-sent' };
      case 'FAILED':
        return { label: 'Echec', icon: ExclamationCircleIcon, className: 'falco-failed' };
      default:
        return { label: 'Non configure', icon: ClockIcon, className: 'falco-none' };
    }
  };

  // Peppol status configuration
  const getPeppolStatusConfig = (status: PeppolStatus) => {
    switch (status) {
      case 'PENDING':
        return { label: 'En attente', icon: ClockIcon, className: 'peppol-pending' };
      case 'DELIVERED':
        return { label: 'Livre', icon: CheckCircleIcon, className: 'peppol-delivered' };
      case 'FAILED':
        return { label: 'Echec', icon: ExclamationCircleIcon, className: 'peppol-failed' };
      case 'NOT_REGISTERED':
        return { label: 'Non inscrit', icon: ExclamationCircleIcon, className: 'peppol-not-registered' };
      default:
        return { label: '-', icon: ClockIcon, className: 'peppol-none' };
    }
  };

  // Check if we should show extended status (only for validated invoices with status data, not drafts)
  const showExtendedStatus = document.type === 'invoice' &&
    !previewMode &&
    (document.falcoStatus || document.peppolStatus || document.structuredCommunication || document.billingVatNumber);

  // Copy state for tiles
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = useCallback(async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  // Helper to truncate ID for display
  const truncateId = (id: string, maxLen = 8) => {
    if (id.length <= maxLen) return id;
    return `${id.substring(0, maxLen)}...`;
  };

  const getAuthToken = (): string | null => {
    return localStorage.getItem('authToken');
  };

  const getPdfEndpoint = () => {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:10007';
    switch (document.type) {
      case 'invoice':
        // Use preview endpoint for drafts (generates PDF with watermark)
        return previewMode
          ? `${baseUrl}/api/billing/invoices/${document.id}/preview-pdf`
          : `${baseUrl}/api/billing/invoices/${document.id}/pdf`;
      case 'devis':
        return `${baseUrl}/api/billing/devis/${document.id}/pdf`;
      case 'creditNote':
        // Use preview endpoint for drafts (generates PDF with watermark)
        return previewMode
          ? `${baseUrl}/api/billing/credit-notes/${document.id}/preview-pdf`
          : `${baseUrl}/api/billing/credit-notes/${document.id}/pdf`;
      default:
        return '';
    }
  };

  useEffect(() => {
    // AbortController to prevent double fetching (React Strict Mode runs effects twice)
    const abortController = new AbortController();
    let objectUrl: string | null = null;

    const loadPdf = async () => {
      // Allow loading if hasPdf is true OR if we're in preview mode (generates on-the-fly)
      if (!document.hasPdf && !previewMode) {
        setError('Aucun PDF disponible pour ce document');
        setLoading(false);
        return;
      }

      try {
        const token = getAuthToken();
        const response = await fetch(getPdfEndpoint(), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status}`);
        }

        const blob = await response.blob();

        // Check if request was aborted before updating state
        if (abortController.signal.aborted) {
          return;
        }

        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('Error loading PDF:', err);
        setError('Erreur lors du chargement du PDF');
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadPdf();

    // Cleanup: abort pending request and revoke object URL
    return () => {
      abortController.abort();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [document.id, document.type, previewMode]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Render through portal to escape HeadlessUI Dialog's focus trap
  return createPortal(
    <div className="pdf-modal-overlay" onClick={onClose}>
      <div className="pdf-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header - Row 1 */}
        <div className="pdf-modal-header">
          {/* Back button - only shown when onBack is provided */}
          {onBack && (
            <button
              className="pdf-modal-back-btn"
              onClick={onBack}
              title={backLabel}
            >
              <ArrowLeftIcon />
              <span>{backLabel}</span>
            </button>
          )}
          <div className="pdf-modal-header-icon">
            <DocumentTextIcon />
          </div>
          <div className="pdf-modal-header-content">
            <h2 className="pdf-modal-title">
              {getDocumentTitle()} {document.number}
            </h2>
            <div className="pdf-modal-meta">
              <span className="pdf-modal-meta-item">
                <CalendarIcon />
                {formatDate(document.date)}
              </span>
              <span className="pdf-modal-meta-item">
                <UserIcon />
                {document.clientName}
              </span>
              <span className="pdf-modal-meta-item amount">
                <CurrencyEuroIcon />
                {formatCurrency(document.amount)}
              </span>
            </div>
          </div>
          <div className="pdf-modal-header-actions">
            {/* Invoice Status Badge - Pill style */}
            <span className={`pdf-modal-status-badge ${getStatusConfig(document.status).className}`}>
              {getStatusConfig(document.status).label}
            </span>
            {document.hasPdf && (
              <button
                className="pdf-modal-action-btn"
                onClick={onDownload}
                title="Telecharger"
              >
                <ArrowDownTrayIcon />
              </button>
            )}
            <button
              className="pdf-modal-close"
              onClick={onClose}
              title="Fermer"
            >
              <XMarkIcon />
            </button>
          </div>
        </div>

        {/* Integration Status Strip - Row 2 (only for invoices) */}
        {showExtendedStatus && (
          <div className="integration-strip">
            <div className="integration-strip-grid">
              {/* Tile 1: Falco/Horus */}
              {document.falcoStatus !== undefined && (
                <div className={`status-tile ${
                  document.falcoStatus === 'UPLOADED' || document.falcoStatus === 'SENT' ? 'status-tile--success' :
                  document.falcoStatus === 'PENDING' || document.falcoStatus === 'PROCESSING' ? 'status-tile--info' :
                  document.falcoStatus === 'FAILED' ? 'status-tile--error' : ''
                }`}>
                  <div className="status-tile-label">
                    <BuildingLibraryIcon className="status-tile-label-icon" />
                    <span>Falco/Horus</span>
                  </div>
                  <div className="status-tile-main">
                    {(() => {
                      const config = getFalcoStatusConfig(document.falcoStatus);
                      const StatusIcon = config.icon;
                      return (
                        <>
                          <StatusIcon className="status-tile-status-icon" />
                          <span>{config.label}</span>
                        </>
                      );
                    })()}
                  </div>
                  <div className="status-tile-meta">
                    {document.falcoSyncedAt && formatDate(document.falcoSyncedAt)}
                    {document.falcoDocumentId && (
                      <span className="status-tile-id">
                        ID: {truncateId(document.falcoDocumentId)}
                      </span>
                    )}
                    {document.falcoError && (
                      <span className="status-tile-error" title={document.falcoError}>
                        {document.falcoError.substring(0, 30)}...
                      </span>
                    )}
                  </div>
                  {document.falcoDocumentId && (
                    <div className="status-tile-actions">
                      <button
                        className="status-tile-action-btn"
                        onClick={() => copyToClipboard(document.falcoDocumentId!, 'falco')}
                        title="Copier ID"
                      >
                        {copiedField === 'falco' ? (
                          <CheckIcon className="status-tile-action-icon copied" />
                        ) : (
                          <ClipboardDocumentIcon className="status-tile-action-icon" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Tile 2: Peppol */}
              {document.peppolStatus !== undefined && (
                <div className={`status-tile ${
                  document.peppolStatus === 'DELIVERED' ? 'status-tile--success' :
                  document.peppolStatus === 'PENDING' ? 'status-tile--info' :
                  document.peppolStatus === 'FAILED' ? 'status-tile--error' :
                  document.peppolStatus === 'NOT_REGISTERED' ? 'status-tile--neutral' : ''
                }`}>
                  <div className="status-tile-label">
                    <GlobeAltIcon className="status-tile-label-icon" />
                    <span>Peppol</span>
                  </div>
                  <div className="status-tile-main">
                    {(() => {
                      const config = getPeppolStatusConfig(document.peppolStatus);
                      const StatusIcon = config.icon;
                      return (
                        <>
                          <StatusIcon className="status-tile-status-icon" />
                          <span>{config.label}</span>
                        </>
                      );
                    })()}
                  </div>
                  <div className="status-tile-meta">
                    {document.peppolDeliveredAt && formatDate(document.peppolDeliveredAt)}
                    {document.peppolStatus === 'NOT_REGISTERED' && (
                      <span>Envoi Peppol indisponible</span>
                    )}
                    {document.peppolError && (
                      <span className="status-tile-error" title={document.peppolError}>
                        {document.peppolError.substring(0, 30)}...
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Tile 3: TVA Client */}
              {document.billingVatNumber && (
                <div className="status-tile status-tile--info">
                  <div className="status-tile-label">
                    <IdentificationIcon className="status-tile-label-icon" />
                    <span>TVA Client</span>
                  </div>
                  <div className="status-tile-main status-tile-main--mono">
                    {document.billingVatNumber}
                  </div>
                  <div className="status-tile-meta">
                    Statut: valide
                  </div>
                  <div className="status-tile-actions">
                    <button
                      className="status-tile-action-btn"
                      onClick={() => copyToClipboard(document.billingVatNumber!, 'vat')}
                      title="Copier"
                    >
                      {copiedField === 'vat' ? (
                        <CheckIcon className="status-tile-action-icon copied" />
                      ) : (
                        <ClipboardDocumentIcon className="status-tile-action-icon" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Tile 4: Communication */}
              {document.structuredCommunication && (
                <div className="status-tile status-tile--info">
                  <div className="status-tile-label">
                    <BanknotesIcon className="status-tile-label-icon" />
                    <span>Communication</span>
                  </div>
                  <div className="status-tile-main status-tile-main--mono status-tile-main--communication">
                    {document.structuredCommunication}
                  </div>
                  <div className="status-tile-meta">
                    Communication structuree
                  </div>
                  <div className="status-tile-actions">
                    <button
                      className="status-tile-action-btn"
                      onClick={() => copyToClipboard(document.structuredCommunication!, 'comm')}
                      title="Copier"
                    >
                      {copiedField === 'comm' ? (
                        <CheckIcon className="status-tile-action-icon copied" />
                      ) : (
                        <ClipboardDocumentIcon className="status-tile-action-icon" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Due Date - only if other tiles exist */}
              {document.dueDate && (
                <div className="status-tile status-tile--neutral">
                  <div className="status-tile-label">
                    <CalendarIcon className="status-tile-label-icon" />
                    <span>Echeance</span>
                  </div>
                  <div className="status-tile-main">
                    {formatDate(document.dueDate)}
                  </div>
                  <div className="status-tile-meta">
                    Date limite de paiement
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* PDF Content */}
        <div className="pdf-modal-body">
          {loading ? (
            <div className="pdf-loading">
              <div className="pdf-spinner"></div>
              <span>Chargement du PDF...</span>
            </div>
          ) : error ? (
            <div className="pdf-error">
              <DocumentTextIcon className="w-16 h-16" />
              <h3>{error}</h3>
              <p>Le document n'a peut-etre pas encore ete genere.</p>
            </div>
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              className="pdf-iframe"
              title={`${getDocumentTitle()} ${document.number}`}
            />
          ) : null}
        </div>

        {/* Validation Footer - for DRAFT documents */}
        {showValidation && onValidate && (
          <div className="pdf-modal-validation-footer">
            <div className="pdf-modal-validation-content">
              <label className="pdf-modal-validation-checkbox">
                <input
                  type="checkbox"
                  checked={sendEmailOnValidate}
                  onChange={(e) => setSendEmailOnValidate(e.target.checked)}
                  disabled={isValidating}
                />
                <span>Envoyer par email apres validation</span>
              </label>
              <button
                className="pdf-modal-validate-btn"
                onClick={() => onValidate(sendEmailOnValidate)}
                disabled={isValidating}
              >
                {isValidating ? (
                  <>
                    <ArrowPathIcon className="animate-spin" />
                    <span>Validation...</span>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon />
                    <span>Valider et finaliser</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    globalThis.document.body
  );
};

export default PdfPreviewModal;
